import React, { useState, useEffect } from "react";
import { User } from "Services";
import { Link } from "react-router-dom";
import Input from "Components/Form/Input";
import Select from "Components/Form/Select";
import UsersApi from "Services/Api/Networking/Users";
import RecordApi from "Services/Api/Cms/Elements/Record"; // Use the same API as Agenda
import { getRecordValue } from "Services/CmsHelpers";
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'Components/Modal';
import PrimaryButton from "Components/Button/PrimaryButton";
import {Checkbox, FileUpload} from "Components/Form";
import {getFallbackAvatarUrl} from "Services/AuthHelpers";
import CountrySelect from "Components/Form/CountrySelect";

const jobTitleKeywords = [
    { value: "Manager", label: "Manager" },
    { value: "Director", label: "Director" },
];

const healthcareTopics = [
    { value: "AI and data", label: "AI and data" },
    { value: "Customer engagement", label: "Customer engagement" },
    { value: "Digital transformation", label: "Digital transformation" },
    { value: "Genomics", label: "Genomics" },
    { value: "Health inequalities & accessible healthcare", label: "Health inequalities & accessible healthcare" },
    { value: "Preventative healthcare", label: "Preventative healthcare" },
    { value: "Sustainable healthcare & healthy environments", label: "Sustainable healthcare & healthy environments" },
];

const networkingObjectives = [
    { value: "Looking for partnerships", label: "Looking for partnerships" },
    { value: "Open to collaboration", label: "Open to collaboration" },
    { value: "Peer learning", label: "Peer learning" },
];

const OrganizationTypeInfo = ({ onClose, onProfileUpdated }) => {
    const [form, setForm] = useState({
        avatar_path: User?.data?.avatar_path || "",
        first_name: User?.data?.first_name || "",
        last_name: User?.data?.last_name || "",
        company_name: User?.data?.company_name || "",
        job_title: User?.data?.job_title || "",
        linkedin: User?.data?.linkedin || "",
        country: User?.data?.country || "",
        consent_networking: User?.data?.consent_networking ?? false,
        jobTitleKeywords: User?.data?.networking_profile?.job_title_keywords?.split(",") || [],
        topicsOfInterest: User?.data?.networking_profile?.topics_of_interest?.split(",") || [],
        networkingObjectives: User?.data?.networking_profile?.networking_objectives?.split(",") || [],
    });
    const [speakers, setSpeakers] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const handleChange = (name, value) => {
        setForm((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleMultiChange = (name, values) => {
        setForm((prev) => ({
            ...prev,
            [name]: values || []
        }));
    };

    useEffect(() => {
        RecordApi.get(null, {
            identifier: 'speakers',
            active: 'Yes',
            page: 1,
            order_by: 'created_at',
            order: 'asc'
        }).then(response => {
            if (response?.success && response.data?.data) {
                setSpeakers(
                    response.data.data.map(speaker => ({
                        value: getRecordValue(speaker, 'title'),
                        label: getRecordValue(speaker, 'title'),
                    }))
                );
            }
        });
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSubmitted(false);
        setFieldErrors({});
        const errorKeyMap = {
            job_title_keywords: "jobTitleKeywords",
            topics_of_interest: "topicsOfInterest",
            networking_objectives: "networkingObjectives",
            first_name: "first_name",
            last_name: "last_name",
            company_name: "company_name",
            job_title: "job_title",
            linkedin: "linkedin",
            consent_networking: "consent_networking",
            country: "country",
        };

        // Convert arrays to comma-separated strings
        const payload = {
            job_title_keywords: Array.isArray(form.jobTitleKeywords) && form.jobTitleKeywords.length > 0
                ? form.jobTitleKeywords.join(',')
                : null,
            topics_of_interest: Array.isArray(form.topicsOfInterest) && form.topicsOfInterest.length > 0
                ? form.topicsOfInterest.join(',')
                : null,
            networking_objectives: Array.isArray(form.networkingObjectives) && form.networkingObjectives.length > 0
                ? form.networkingObjectives.join(',')
                : null,
            first_name: form.first_name,
            last_name: form.last_name,
            company_name: form.company_name,
            job_title: form.job_title,
            linkedin: form.linkedin,
            consent_networking: form.consent_networking,
            avatar_path: form.avatar_path,
            country: form.country,
        };
        try {
            const response = await UsersApi.saveProfile(payload);

            if (response?.status && response.status !== 200) {
                let message = response.message || "An error occurred while saving your profile.";
                if (response.errors) {
                    const mappedErrors = {};
                    Object.entries(response.errors).forEach(([key, value]) => {
                        mappedErrors[errorKeyMap[key] || key] = value;
                    });
                    setFieldErrors(mappedErrors);
                }
                setError(message);
                setSubmitted(false);
                return;
            }

            if (response?.status && response.status === 200) {
                User.refresh();
                setSubmitted(true);
                if (onProfileUpdated) onProfileUpdated();
            }

            setSubmitted(true);
        } catch (err) {
            setSubmitted(false);
            let message = "An error occurred while saving your profile.";
            if (err?.response?.data?.message) {
                message = err.response.data.message;
            }
            if (err?.response?.data?.errors) {
                setFieldErrors(err.response.data.errors);
                message += " " + Object.values(err.response.data.errors).flat().join(" ");
            }
            setError(message);
        }
    };

    if (submitted && !error) {
        return (
            <Modal onClose={onClose}>
                <ModalHeader className="font-bold text-lg text-app-blue mb-2 text-left">
                    Profile updated!
                </ModalHeader>
                <ModalBody className="text-app-blue text-left mb-4">
                    <div className="bg-white border border-app-blue rounded p-4 mb-4 text-app-blue text-center">
                        <div>Your networking profile has been saved.</div>
                    </div>
                </ModalBody>
                <ModalFooter className="flex justify-end">
                    <PrimaryButton text={"Close"} onClick={onClose} />
                </ModalFooter>
            </Modal>
        );
    }

    return (
        <Modal>
            <ModalHeader className="font-bold text-lg text-app-blue mb-2 text-left" onClose={onClose}>
                Edit your networking profile:
            </ModalHeader>

            <ModalBody className="text-app-blue text-left mb-4">

                <form
                    className="p-6 space-y-6"
                    onSubmit={handleSubmit}
                    autoComplete="off"
                >
                    <FileUpload
                        containerClassName="m-8 mt-4 flex flex-col justify-center items-center gap-4"
                        value={form.avatar_path}
                        onChange={v => handleChange("avatar_path", v)}
                        accept="image/*"
                        placeholder="Update Picture"
                        fallbackImage={getFallbackAvatarUrl(form)}
                    />
                    <div className="my-auto py-4 text-white">
                        <Checkbox
                            label="I am happy to connect with other attendees"
                            value={form.consent_networking === true}
                            id="consent_networking"
                            error={fieldErrors?.consent_networking}
                            onChange={v => handleChange("consent_networking", v)}
                            containerClassName="font-bold text-2xl"
                            className="font-bold text-xl"
                            textColor="text-white"
                        />
                    </div>
                    <Input
                        labelColour="text-app-blue"
                        label="First Name"
                        placeholder="First Name"
                        value={form.first_name}
                        id="first_name"
                        error={fieldErrors?.first_name}
                        onChange={v => handleChange("first_name", v)}
                        containerClassName="col-span-2 md:col-span-1"
                    />
                    <Input
                        labelColour="text-app-blue"
                        label="Last Name"
                        placeholder="Last Name"
                        value={form.last_name}
                        id="last_name"
                        error={fieldErrors?.last_name}
                        onChange={v => handleChange("last_name", v)}
                        containerClassName="col-span-2 md:col-span-1"
                    />
                    <Input
                        labelColour="text-app-blue"
                        label="Company"
                        placeholder="Company"
                        value={form.company_name}
                        id="company_name"
                        error={fieldErrors?.company_name}
                        onChange={v => handleChange("company_name", v)}
                        containerClassName="col-span-2 md:col-span-1"
                    />
                    <Input
                        labelColour="text-app-blue"
                        label="Your Job Title"
                        placeholder="Job Title"
                        value={form.job_title}
                        id="job_title"
                        error={fieldErrors?.job_title}
                        onChange={v => handleChange("job_title", v)}
                        containerClassName="col-span-2 md:col-span-1"
                    />
                    <Input
                        labelColour="text-app-blue"
                        label="LinkedIn Profile URL"
                        placeholder="LinkedIn Profile URL"
                        value={form.linkedin}
                        id="linkedin"
                        error={fieldErrors?.linkedin}
                        onChange={v => handleChange("linkedin", v)}
                        containerClassName="col-span-2 md:col-span-1"
                    />
                    <CountrySelect
                        value={ form?.country ?? null }
                        onChange={ ( v ) => handleChange( "country", v ) }
                        classes={ `w-full p-2 border border-app-blue rounded` }
                        label="Country*"
                        error={ fieldErrors?.country }
                        labelColour="text-app-blue"
                    />
                    {form.consent_networking && (
                        <>
                            <p className={"text-app-blue font-bold"}>
                                The following questions will help us to connect you with the right people at the event.
                            </p>

                            <Select
                                label="Job Title Keywords"
                                name="jobTitleKeywords"
                                options={jobTitleKeywords}
                                value={form.jobTitleKeywords}
                                onChange={v => handleChange("jobTitleKeywords", v)}
                                isMulti
                                labelColour="text-app-blue"
                                error={fieldErrors?.jobTitleKeywords}
                                containerClassName="col-span-2 md:col-span-1"
                            />
                            <Select
                                label="Event Topics of Interest (select 1–3)"
                                name="topicsOfInterest"
                                options={healthcareTopics}
                                value={form.topicsOfInterest}
                                onChange={v => handleMultiChange("topicsOfInterest", v.slice(0, 3))}
                                isMulti
                                labelColour="text-app-blue"
                                error={fieldErrors?.topicsOfInterest}
                                containerClassName="col-span-2 md:col-span-1"
                            />
                            <Select
                                label="Networking Objectives"
                                name="networkingObjectives"
                                options={networkingObjectives}
                                value={form.networkingObjectives}
                                onChange={v => handleMultiChange("networkingObjectives", v)}
                                isMulti
                                labelColour="text-app-blue"
                                error={fieldErrors?.networkingObjectives}
                                containerClassName="col-span-2 md:col-span-1"
                            />
                        </>
                    )}

                    <div className="flex justify-between">
                        <PrimaryButton
                            type="submit"
                            text={"Save Questions"}
                        >
                            Save Questions
                        </PrimaryButton>
                    </div>

                    {submitted && !error && (
                        <div className="text-app-blue text-left font-bold">
                            Profile updated! Your networking profile has been saved.
                        </div>
                    )}
                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-2 text-left">
                            {error}
                        </div>
                    )}
                </form>
            </ModalBody>
        </Modal>
    );
};

export default OrganizationTypeInfo;
